/**
 * B-418 — „Wyślij e-mailem” przy alercie CEPiK (okno alertu i karta w Kierowcach).
 *
 * Adresaci: użytkownicy systemu (szybki wybór z listy mailingowej z ustawień albo
 * wyszukanie po nazwisku) oraz — B-420 — adresy spoza systemu, ale WYŁĄCZNIE z listy
 * prowadzonej przez administratora w ustawieniach (dowolnego adresu nie da się wpisać).
 * Wysyłka NIE zamyka alertu — potwierdzenie z notatką zostaje osobno.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Mail, Loader2, Plus, Search, Send, X } from 'lucide-react';
import { api } from '@/shared/lib/api';
import { cn } from '@/shared/lib/utils';
import { fmtDateTime, errMsg, type CepikAlert } from './cepikShared';

interface Candidate { id: string; name: string; email: string; external?: boolean }
const EXT_TAG = 'rounded bg-amber-100 px-1 text-[9px] font-bold uppercase text-amber-800 dark:bg-amber-500/15 dark:text-amber-200';
const MAX_TO = 10;

/** Historia wysyłek alertu — kto, kiedy, do kogo. */
export function AlertMailHistory({ a, className }: { a: CepikAlert; className?: string }) {
  if (!a.emails?.length) return null;
  return (
    <div className={cn('space-y-0.5 text-[11px] text-muted-foreground', className)}>
      {a.emails.map((e, i) => (
        <p key={i} className="flex items-start gap-1.5">
          <Mail className="mt-0.5 h-3 w-3 shrink-0 text-sky-600" />
          <span>
            {fmtDateTime(e.at)} · <b className="text-foreground">{e.byName}</b> wysłał(a) do {e.to.map((t) => (t.external ? `${t.name} (spoza systemu)` : t.name)).join(', ')}
            {e.note ? ` — „${e.note}”` : ''}
            {e.sent < e.to.length ? <span className="text-rose-600"> (doręczono {e.sent} z {e.to.length})</span> : null}
          </span>
        </p>
      ))}
    </div>
  );
}

/** Przycisk + rozwijany formularz wysyłki. `onSent` — np. żeby okno zostało na tym samym alercie. */
export function AlertMailButton({ a, compact, onOpenChange }: { a: CepikAlert; compact?: boolean; onOpenChange?: (open: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const toggle = (v: boolean) => { setOpen(v); onOpenChange?.(v); };
  return (
    <>
      <button type="button" onClick={() => toggle(!open)} aria-expanded={open}
        className={cn('inline-flex items-center gap-1.5 rounded-lg border font-medium hover:bg-muted',
          compact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm', open && 'bg-muted')}>
        <Mail className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} /> Wyślij e-mailem
      </button>
      {open && <AlertMailForm a={a} onClose={() => toggle(false)} />}
    </>
  );
}

export function AlertMailForm({ a, onClose }: { a: CepikAlert; onClose: () => void }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Candidate[]>([]);
  const [q, setQ] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const listQ = useQuery({
    queryKey: ['cepik', 'mail-candidates', ''],
    queryFn: () => api.get<{ list: Candidate[]; found: Candidate[] }>('/cepik-verification/alerts/mail-candidates'),
    staleTime: 60_000,
  });
  const searchQ = useQuery({
    queryKey: ['cepik', 'mail-candidates', q.trim()],
    queryFn: () => api.get<{ list: Candidate[]; found: Candidate[] }>('/cepik-verification/alerts/mail-candidates', { search: q.trim() }),
    enabled: q.trim().length >= 2,
  });
  const has = (id: string) => picked.some((p) => p.id === id);
  const add = (c: Candidate) => setPicked((prev) => (prev.some((p) => p.id === c.id) || prev.length >= MAX_TO ? prev : [...prev, c]));
  const list = listQ.data?.list ?? [];
  const found = searchQ.data?.found ?? [];

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ sent: number; recipients: number }>(`/cepik-verification/alerts/${a.id}/email`, {
        userIds: picked.map((p) => p.id), note,
      });
      if (r.sent === r.recipients) toast.success(`Wysłano do ${r.recipients} ${r.recipients === 1 ? 'osoby' : 'osób'}`);
      else toast.warning(`Serwer poczty przyjął ${r.sent} z ${r.recipients} wiadomości — szczegóły w dzienniku e-maili`);
      qc.invalidateQueries({ queryKey: ['cepik'] });
      onClose();
    } catch (e) { toast.error(errMsg(e, 'Nie udało się wysłać')); }
    finally { setBusy(false); }
  };

  return (
    <div className="w-full space-y-2 rounded-xl border bg-muted/30 p-3 text-sm">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-sky-600" />
        <span className="font-semibold">Wyślij ten alert e-mailem</span>
        <button type="button" onClick={onClose} className="ml-auto rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Zamknij">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex min-h-[2rem] flex-wrap items-center gap-1.5">
        {picked.length ? picked.map((p) => (
          <span key={p.id} className={cn('inline-flex items-center gap-1 rounded-full py-0.5 pl-2.5 pr-1 text-xs font-semibold',
            p.external ? 'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200' : 'bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-200')} title={p.email}>
            {p.name}{p.external && <span className={EXT_TAG}>spoza systemu</span>}
            <button type="button" onClick={() => setPicked((prev) => prev.filter((x) => x.id !== p.id))} className="rounded-full p-0.5 hover:bg-sky-200 dark:hover:bg-sky-500/30" aria-label={`Usuń ${p.name}`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        )) : <span className="text-xs text-muted-foreground">Wybierz adresatów — z listy poniżej albo wyszukaj osobę.</span>}
      </div>

      {list.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase text-muted-foreground">Lista z ustawień:</span>
          {list.filter((c) => !has(c.id)).map((c) => (
            <button key={c.id} type="button" onClick={() => add(c)} title={c.external ? `${c.email} — adres spoza systemu (z listy w ustawieniach)` : c.email}
              className={cn('inline-flex items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs hover:bg-muted', c.external && 'border-amber-300 dark:border-amber-500/40')}>
              <Plus className={cn('h-3 w-3', c.external ? 'text-amber-600' : 'text-sky-600')} />{c.name}
              {c.external && <span className={EXT_TAG}>spoza systemu</span>}
            </button>
          ))}
          {list.some((c) => !c.external && !has(c.id)) && (
            <button type="button" onClick={() => list.filter((c) => !c.external).forEach(add)} className="text-xs font-semibold text-sky-700 hover:underline dark:text-sky-300">wszyscy użytkownicy z listy</button>
          )}
        </div>
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Dodaj osobę — nazwisko lub e-mail"
          className="w-full rounded-lg border bg-background py-1.5 pl-8 pr-2 text-sm" />
        {q.trim().length >= 2 && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border bg-popover shadow-lg">
            {searchQ.isLoading ? <p className="px-3 py-2 text-xs text-muted-foreground">Szukam…</p>
              : !found.length ? <p className="px-3 py-2 text-xs text-muted-foreground">Brak aktywnych użytkowników o takim nazwisku lub adresie</p>
                : found.map((c) => (
                  <button key={c.id} type="button" disabled={has(c.id)} onClick={() => { add(c); setQ(''); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-40">
                    <Plus className="h-3.5 w-3.5 text-sky-600" />
                    <span className="min-w-0 flex-1 truncate">{c.name} <span className="text-xs text-muted-foreground">{c.email}</span></span>
                  </button>
                ))}
          </div>
        )}
      </div>

      <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} rows={2}
        placeholder="Wiadomość (nieobowiązkowa), np. proszę o pilne odsunięcie od służby i kontakt z kierowcą"
        className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400/40" />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-muted-foreground">
          Wysyłka nie zamyka alertu. Najwyżej {MAX_TO} adresatów naraz.
          {picked.some((p) => p.external) ? ' Adresat spoza systemu dostanie dane kierowcy bez linku do panelu.' : ''}
        </span>
        <button type="button" onClick={send} disabled={busy || !picked.length}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Wyślij{picked.length ? ` (${picked.length})` : ''}
        </button>
      </div>
    </div>
  );
}
